import { Dispatch, SetStateAction, useEffect, useState } from "react";
import LocalStoragePersistence from "../../localStoragePersistence/common/LocalStoragePersistence";
import { LocalStorageKey } from "../../localStoragePersistence/common/LocalStorageKey";
import { Result } from "neverthrow";
import { ParseError } from "../../dto/parsing/JsonParsing";

export function useStateWithLocalStoragePersistenceAndDefault<S>(
  localStorageKey: LocalStorageKey,
  defaultValue: S,
): [S, Dispatch<SetStateAction<S>>] {
  const [value, setValue] = useState<S>(() => {
    const v = unwrapWithErrorLogging<S>(
      LocalStoragePersistence.read(localStorageKey),
    );
    return v ?? defaultValue;
  });

  useEffect(() => {
    LocalStoragePersistence.persist(localStorageKey, value);
  }, [localStorageKey, value]);

  return [value, setValue];
}

export function useStateWithLocalStoragePersistence<S>(
  localStorageKey: LocalStorageKey,
): [S | undefined, Dispatch<SetStateAction<S | undefined>>] {
  const [value, setValue] = useState<S | undefined>(() =>
    unwrapWithErrorLogging<S>(LocalStoragePersistence.read(localStorageKey)),
  );

  useEffect(() => {
    LocalStoragePersistence.persist(localStorageKey, value);
  }, [value, localStorageKey]);

  return [value, setValue];
}

const unwrapWithErrorLogging = <T>(
  v: Result<T | undefined, ParseError>,
): T | undefined => {
  return v
    .orTee((e) =>
      console.error("Could not read default value. Returning undefined.", e),
    )
    .unwrapOr(undefined);
};
